import { useState } from 'react'
import { CalendarCheck, X } from 'lucide-react'
import { Card } from '@/shared/ui/card'
import { Skeleton } from '@/shared/ui/skeleton'
import { useCourtBookings, useCancelCourtBooking, type CourtBooking } from './use-court-bookings'

/**
 * «Pistas reservadas» — court bookings this player made through the app.
 *
 * Sits above the match list on Mis partidos rather than on a screen of its own.
 * A player books one or two courts a week; a whole tab for it would be empty
 * most of the time, and the two things belong together anyway — a reserved
 * court is usually the reason a match exists.
 *
 * ── ABSENT WHEN THERE IS NOTHING, NOT EMPTY ─────────────────────────────────
 * Almost nobody has a court booking: it needs a club that switched the module
 * on, and 0 of 13 production clubs have. An empty "no tienes pistas
 * reservadas" panel above every player's matches would advertise a feature
 * that, for them, does not exist. It appears when there is something in it.
 *
 * That is also why a failed READ renders nothing rather than an error: this is
 * a section a player did not ask for, and the screen it sits on is complete
 * without it. The CANCEL path is the opposite — that one is a request the
 * player made, and it always answers.
 */
export function CourtBookingsSection() {
  const { data, isPending, isError } = useCourtBookings()
  const cancel = useCancelCourtBooking()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Cancelled ones stay out: the player asked for them to be gone, and a
  // struck-through row is a reminder of something they already dealt with.
  const upcoming = (data ?? [])
    .filter((b) => b.status === 'confirmed' && b.startMs > Date.now())
    .sort((a, b) => a.startMs - b.startMs)

  if (isPending) {
    return (
      <div className="px-4 pb-3" aria-busy>
        <Skeleton className="h-[72px] w-full rounded-[var(--radius-card)]" />
      </div>
    )
  }
  // error-not-empty-ok: an unreadable list and an empty one render the SAME
  // here, and that is the decision rather than an oversight. This section is
  // additive — a player with no court bookings (almost everyone) sees nothing,
  // so an error state would be a panel appearing only when something broke,
  // announcing a feature they do not use. Their matches are untouched below.
  // The cancel path, which IS a request the player made, always answers.
  // Keep a known reservation visible if a background refresh fails.
  if ((isError && !data) || upcoming.length === 0) return null

  async function onCancel(b: CourtBooking) {
    setBusy(b.id)
    setError(null)
    const res = await cancel(b)
    setBusy(null)
    if (!res.ok) setError(res.message)
  }

  return (
    <div className="flex flex-col gap-2 px-4 pb-3">
      <span className="text-[length:var(--fs-sm)] font-semibold text-[color:var(--fg-muted)]">
        Pistas reservadas
      </span>
      {upcoming.map((b) => (
        <Card key={b.id}>
          <div className="flex items-center gap-3 p-4">
            <CalendarCheck size={18} className="shrink-0 text-[color:var(--ok)]" />
            <div className="min-w-0 flex-1">
              <span className="block font-semibold tabular-nums">{whenLabel(b.startMs)}</span>
              <span className="block text-[length:var(--fs-xs)] text-[color:var(--fg-muted)]">
                {b.courtName} · {b.durationMinutes} min · se paga en el club
              </span>
            </div>
            <button
              type="button"
              onClick={() => void onCancel(b)}
              disabled={busy === b.id}
              aria-label={`Cancelar la pista de ${whenLabel(b.startMs)}`}
              className="flex min-h-[var(--tap)] min-w-[var(--tap)] items-center justify-center rounded-[var(--radius)] text-[color:var(--fg-muted)] hover:bg-[var(--line-soft)] disabled:opacity-40"
            >
              {busy === b.id ? '…' : <X size={18} />}
            </button>
          </div>
        </Card>
      ))}
      {error && (
        <span role="alert" className="text-[length:var(--fs-xs)] text-[color:var(--warn)]">
          {error}
        </span>
      )}
    </div>
  )
}

/**
 * The player's own local reading of the slot.
 *
 * The device zone is CORRECT here, unlike everywhere else in this feature: the
 * club's clock decides which day a slot belongs to and what it costs, but
 * "when do I need to be there" is a question about the reader's own calendar.
 * A player in Madrid looking at a Granada booking wants their own 19:00.
 */
function whenLabel(ms: number): string {
  return new Intl.DateTimeFormat('es-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(ms))
}
